import { useEffect, useMemo, useRef, useState } from "react"
import { animate, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./FeatherweightPaywall.module.scss"

/**
 * FeatherweightPaywall — an original, self-contained, NON-privacy paywall.
 *
 * Divergence brief (see claude-paywalls/_ledger.md): the prior eight paywalls
 * clustered hard — almost all pitched PRIVACY on a DARK, SKEUOMORPHIC,
 * centered-hero+glowing-card skeleton, mostly passive. Featherweight moves
 * every axis at once:
 *   - Value frame:   STORAGE-SPACE — "free up your phone", not privacy.
 *   - Layout:        an interactive CALCULATOR / data-viz dashboard — two
 *                    storage bars and a draggable slider, no hero card.
 *   - Interaction:   INPUT-DRIVEN — drag the slider for how many photos you
 *                    have and watch gigabytes drain into the cloud live.
 *   - Register:      PLAYFUL / light.
 *   - Art direction: LIGHT, FLAT, bold EDITORIAL-TYPE + DATA-VIZ. No dark
 *                    theme, no skeuomorphism, no photos (Recall already went
 *                    photographic — data-viz is the more divergent move and
 *                    the storage frame is inherently quantitative).
 *
 * The whole screen is the value made tangible: your phone is mostly photos,
 * move the originals to the cloud at full quality, reclaim the gigabytes, and
 * never lose a single memory. Yearly is the prominent price.
 */

// Storage model (deliberately simple, honest-feeling numbers).
const PHONE_GB = 128 // assumed device size for the visual
const SYSTEM_GB = 34 // apps + system + everything-that-isn't-photos
const MB_PER_PHOTO = 3.4 // average modern photo (HEIC/JPEG mix)

const MIN_PHOTOS = 1200
const MAX_PHOTOS = 48000
const DEFAULT_PHOTOS = 14200

const photosToGb = (n: number) => (n * MB_PER_PHOTO) / 1024

const clampGb = (gb: number) => Math.min(gb, PHONE_GB - SYSTEM_GB)

const FeatherweightPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [photos, setPhotos] = useState(DEFAULT_PHOTOS)

  // Derived storage figures.
  const photosGb = clampGb(photosToGb(photos))
  const usedBeforePct = ((SYSTEM_GB + photosGb) / PHONE_GB) * 100
  const photosPct = (photosGb / PHONE_GB) * 100
  const systemPct = (SYSTEM_GB / PHONE_GB) * 100

  // The animated "freed" counter — springs toward photosGb whenever the
  // slider changes, so the number feels like it's draining/filling.
  const [freedDisplay, setFreedDisplay] = useState(photosGb)
  const counterRef = useRef(photosGb)
  useEffect(() => {
    if (reduceMotion) {
      counterRef.current = photosGb
      setFreedDisplay(photosGb)
      return
    }
    const controls = animate(counterRef.current, photosGb, {
      duration: 0.5,
      ease: "easeOut",
      onUpdate: (v) => {
        counterRef.current = v
        setFreedDisplay(v)
      },
    })
    return controls.stop
  }, [photosGb, reduceMotion])

  // One-time auto-demo: sweep the slider so the mechanic announces itself,
  // then settle back to the default and invite the user to grab it.
  const demoedRef = useRef(false)
  useEffect(() => {
    if (reduceMotion || demoedRef.current) return
    demoedRef.current = true
    const controls = animate(MIN_PHOTOS + 2600, DEFAULT_PHOTOS, {
      duration: 1.4,
      delay: 0.45,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setPhotos(Math.round(v)),
    })
    return controls.stop
  }, [reduceMotion])

  const handleClose = () => native.postNativeEvent("close")
  const handlePurchase = () => {
    const product: Product | undefined =
      plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const selectedHasTrial =
    plan === "yearly"
      ? (yearlyProduct?.hasTrial ?? true)
      : (monthlyProduct?.hasTrial ?? false)

  const freedRounded = Math.round(freedDisplay)
  const photoCountLabel = useMemo(
    () => photos.toLocaleString("en-US"),
    [photos],
  )

  // The phone glyph "floats up" as more weight is offloaded — a light,
  // literal read on the name. Maps 0..maxGb of photos to a small lift.
  const maxPhotosGb = PHONE_GB - SYSTEM_GB
  const lift = -(photosGb / maxPhotosGb) * 14

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.wordmark}>
          <span className={styles.mark} aria-hidden>
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M4 17c5-1 9-5 13-11 0 6-3 11-9 12-2 .3-3 .2-4 .1-.3-.4-.3-.8 0-1.2z"
                fill="currentColor"
              />
              <path
                d="M6 18c3.5-2.5 6-5.5 8-9"
                fill="none"
                stroke="var(--fw-paper)"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
          </span>
          Featherweight
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.3"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <main className={styles.content}>
        <h1 className={styles.headline}>
          Your phone is mostly
          <br />
          <span className={styles.accent}>photos.</span>
        </h1>
        <p className={styles.sub}>
          Move the originals to the cloud — full quality, every one — and take
          your storage back.
        </p>

        <section className={styles.calc} aria-label="Storage calculator">
          <motion.div
            className={styles.phone}
            animate={{ y: reduceMotion ? 0 : lift }}
            transition={{ type: "spring", stiffness: 120, damping: 14 }}
            aria-hidden
          >
            <div className={styles.phoneScreen}>
              <span className={styles.phoneCap}>{PHONE_GB} GB</span>
            </div>
          </motion.div>

          <div className={styles.bigNumber}>
            <span className={styles.freed}>{freedRounded}</span>
            <span className={styles.gb}>GB</span>
            <span className={styles.freedLabel}>back on your phone</span>
          </div>

          <div className={styles.bars}>
            <div className={styles.barRow}>
              <span className={styles.barTag}>Today</span>
              <div className={styles.track}>
                <div
                  className={styles.segSystem}
                  style={{ width: `${systemPct}%` }}
                />
                <motion.div
                  className={styles.segPhotos}
                  animate={{ width: `${photosPct}%` }}
                  transition={{ type: "spring", stiffness: 160, damping: 22 }}
                />
              </div>
              <span className={styles.barPct}>
                {Math.round(usedBeforePct)}%
              </span>
            </div>

            <div className={styles.barRow}>
              <span className={styles.barTag}>With us</span>
              <div className={styles.track}>
                <div
                  className={styles.segSystem}
                  style={{ width: `${systemPct}%` }}
                />
                <motion.div
                  className={styles.segFreed}
                  animate={{ width: `${photosPct}%` }}
                  transition={{ type: "spring", stiffness: 160, damping: 22 }}
                />
              </div>
              <span className={styles.barPct}>{Math.round(systemPct)}%</span>
            </div>

            <div className={styles.legend}>
              <span>
                <i className={styles.dotSystem} /> Apps &amp; system
              </span>
              <span>
                <i className={styles.dotPhotos} /> Photos on device
              </span>
              <span>
                <i className={styles.dotFreed} /> Freed up
              </span>
            </div>
          </div>

          <div className={styles.sliderWrap}>
            <label className={styles.sliderLabel} htmlFor="fw-photos">
              <span>📸 {photoCountLabel} photos</span>
              <span className={styles.sliderHint}>
                drag to match your phone
              </span>
            </label>
            <input
              id="fw-photos"
              className={styles.slider}
              type="range"
              min={MIN_PHOTOS}
              max={MAX_PHOTOS}
              step={100}
              value={photos}
              onChange={(e) => setPhotos(Number(e.target.value))}
            />
          </div>
        </section>

        <p className={styles.reassure}>
          Your memories never leave you. Originals stay safe in the cloud at
          full resolution — clearing the local copies just gives your phone room
          to breathe.
        </p>

        <section className={styles.pricing}>
          <button
            type="button"
            className={`${styles.planRow} ${plan === "yearly" ? styles.planActive : ""}`}
            onClick={() => setPlan("yearly")}
          >
            <div className={styles.planLeft}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.planNote}>
                {yearlyPerMonth}/mo · billed annually
              </span>
            </div>
            <div className={styles.planRight}>
              <span className={styles.planPrice}>{yearlyPrice}</span>
              <span className={styles.planUnit}>/year</span>
            </div>
            <span className={styles.bestTag}>Best value</span>
          </button>

          <button
            type="button"
            className={`${styles.planRowSmall} ${plan === "monthly" ? styles.planActiveSmall : ""}`}
            onClick={() => setPlan("monthly")}
          >
            <span className={styles.planNameSmall}>Monthly</span>
            <span className={styles.planPriceSmall}>
              {monthlyPrice}
              <span className={styles.planUnitSmall}>/mo</span>
            </span>
          </button>
        </section>

        <p className={styles.frame}>
          A bigger phone costs hundreds. Breathing room costs {yearlyPerMonth} a
          month.
        </p>

        <button className={styles.cta} onClick={handlePurchase}>
          {selectedHasTrial
            ? "Try it free — reclaim my space"
            : "Reclaim my space"}
        </button>
        <p className={styles.ctaSub}>
          {plan === "yearly"
            ? selectedHasTrial
              ? `7 days free, then ${yearlyPrice}/year. Cancel anytime.`
              : `${yearlyPrice}/year · ${yearlyPerMonth}/mo. Cancel anytime.`
            : `${monthlyPrice}/month. Cancel anytime.`}
        </p>

        <footer className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </footer>
      </main>
    </div>
  )
}

export default FeatherweightPaywall
